import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface ListingView {
  id: string;
  seller_id: string;
  item_ref: string;
  quantity: number;
  price_per_unit: number;
  currency: string;
  listing_type: string;
  status: string;
  created_at: number;
  expires_at: number;
}

interface MarketSummary {
  total_active_listings: number;
  total_transactions: number;
}

export default function Trading({ session }: Props) {
  const playerId = session.playerId;
  const [listings, setListings] = useState<ListingView[]>([]);
  const [summary, setSummary] = useState<MarketSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create listing form
  const [itemRef, setItemRef] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('gold');

  const base = `/api/v1/games/${session.gameId}/modules/trading`;

  const reloadListings = () => {
    fetch(`${base}/listings`)
      .then(r => r.json())
      .then((d: { listings: ListingView[] }) => setListings(d.listings))
      .catch(e => setError(e.message));
  };

  const reloadSummary = () => {
    fetch(`${base}/summary`)
      .then(r => r.json())
      .then((d: { summary: MarketSummary }) => setSummary(d.summary))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadListings(); reloadSummary(); }, [session.gameId]);

  const createListing = async () => {
    if (!itemRef.trim() || !price.trim()) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/listings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          seller_id: playerId,
          item_ref: itemRef.trim(),
          quantity: parseInt(quantity, 10) || 1,
          price: parseInt(price, 10) || 0,
          currency: currency.trim(),
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setItemRef(''); setPrice(''); setQuantity('1');
      reloadListings(); reloadSummary();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const buyListing = async (listingId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/listings/${listingId}/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ buyer_id: playerId }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadListings(); reloadSummary();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const cancelListing = async (listingId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/listings/${listingId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_id: playerId }),
      });
      if (!res.ok) throw new Error(await res.text());
      reloadListings(); reloadSummary();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="Trading"
        subtitle="Marketplace listings, buy, sell, and cancel."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Market Summary */}
      {summary && (
        <div className="card mb-5 p-4">
          <h3 className="text-sm font-semibold mb-3">Market Summary</h3>
          <div className="flex gap-6">
            <div>
              <div className="text-xs text-fg-muted">Active Listings</div>
              <div className="text-lg font-semibold">{summary.total_active_listings}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted">Total Transactions</div>
              <div className="text-lg font-semibold">{summary.total_transactions}</div>
            </div>
          </div>
        </div>
      )}

      {/* Create Listing */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Create Listing</h3>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Item Ref</label>
            <input className="input" placeholder="iron_sword" value={itemRef} onChange={e => setItemRef(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Quantity</label>
            <input className="input w-20" type="number" min="1" value={quantity} onChange={e => setQuantity(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Price</label>
            <input className="input w-24" type="number" min="1" placeholder="100" value={price} onChange={e => setPrice(e.target.value)} />
          </div>
          <div>
            <label className="text-xs text-fg-muted block mb-1">Currency</label>
            <input className="input w-24" placeholder="gold" value={currency} onChange={e => setCurrency(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !itemRef.trim() || !price.trim()} onClick={createListing}>List</button>
        </div>
      </div>

      {/* Active Listings */}
      {listings.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold p-4 pb-2">Active Listings</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Seller</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Currency</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map(l => (
                  <tr key={l.id}>
                    <td className="font-mono text-xs">{l.id}</td>
                    <td className="font-mono text-xs">{l.seller_id.slice(0, 8)}...</td>
                    <td>{l.item_ref}</td>
                    <td>{l.quantity}</td>
                    <td>{l.price_per_unit}</td>
                    <td>{l.currency}</td>
                    <td className="flex gap-2">
                      <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => buyListing(l.id)}>Buy</button>
                      <button className="btn btn-sm" disabled={busy} onClick={() => cancelListing(l.id)}>Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {listings.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No active listings</div>
          <div className="text-xs text-fg-muted">Create a listing above to get started.</div>
        </div>
      )}
    </Container>
  );
}
